import ErrorIcon from '@curology/ui-components-web/react/Icon/icons/ErrorIcon';
import React from 'react';

import { type FlashMessageProps } from '@/atoms/flashMessages/flashMessagesState';
import { type Brand } from '@/consts/brands';
import { PAYMENT_LINE_ITEM_LABELS } from '@/consts/payments';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { getPaymentErrorMessage } from '@/helpers/productSelectionAndCheckout/getPaymentErrorMessage';
import { renderFlashMessageOnCheckoutSuccess } from '@/helpers/productSelectionAndCheckout/helpers';
import { reportUnhandledPaymentErrorToSentry } from '@/helpers/productSelectionAndCheckout/reportUnhandledPaymentErrorToSentry';
import { updateRegistrationEligibility } from '@/services/eligibilityService/actions';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { type IneligibleReasonType } from '@/types/eligibility/eligibility';
import {
  type ActivateExpressCheckoutData,
  type Flags,
  type Patient,
  type ValidationErrorResponse,
} from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { type HttpError } from '@/utils/http/client';

// Logic comes from:
// resources/assets/jsx/components/browserPaymentMethodButton/index.tsx
// resources/assets/jsx/components/browserPaymentMethodButton/BrowserPaymentMethods.ts.
// resources/assets/jsx/signup/views/recommendations/components/newCurologyControlExperience/js/useApplePayExpressCheckoutHandler.tsx

export const applePayButtonContainerId = 'apple-pay-button-container';
export const applePayButtonId = 'apple-pay-button';

export const applePayButtonStyleConfig: {
  borderRadius: string;
  height: string;
  theme: 'dark' | 'light' | 'light-outline';
  type: 'default' | 'donate' | 'buy';
} = {
  borderRadius: '26px',
  height: '52px',
  theme: 'dark',
  type: 'default',
};

const getTotalLabel = (brand: Brand): string => {
  switch (brand) {
    case 'curology':
      return PAYMENT_LINE_ITEM_LABELS.CUROLOGY_TOTAL;
    case 'agency':
      return PAYMENT_LINE_ITEM_LABELS.AGENCY_TOTAL;
    default:
      throw new Error(`Unknown brand ${brand}`);
  }
};

export const getPaymentRequestOptions = (
  brand: Brand,
  flags: Flags,
  futureShipmentTotal: number,
  handling: number,
  shipping: number,
  tax: number,
  trialDiscountTotal: number,
  trialTotalWithoutDiscounts: number,
  trialTotalWithTax: number,
) => {
  const sAndH = shipping + handling;

  const country = 'US';
  const currency = 'usd';

  const displayItems = [
    {
      amount: trialTotalWithoutDiscounts,
      label: flags.priceSummaryHeaderText || '30-day trial box',
    },
    {
      amount: -trialDiscountTotal,
      label: 'Discount',
    },
    {
      amount: sAndH,
      label: 'Shipping',
    },
    {
      amount: tax,
      label: 'Handling fee',
    },
    {
      amount: futureShipmentTotal,
      label: 'Your next box',
    },
  ];

  const requestPayerEmail = false;
  const requestPayerName = true;
  const requestPayerPhone = true;
  const requestShipping = true;

  const shippingOptions = [
    {
      amount: sAndH,
      id: 'basic',
      label: 'Ground Shipping',
    },
  ];

  const total = {
    amount: trialTotalWithTax,
    label: getTotalLabel(brand),
  };

  return {
    country,
    currency,
    displayItems,
    requestPayerEmail,
    requestPayerName,
    requestPayerPhone,
    requestShipping,
    shippingOptions,
    total,
  };
};

const getActivateExpressCheckoutTrialData = (
  patient: Patient,
  event: stripe.paymentRequest.StripeTokenPaymentResponse,
): ActivateExpressCheckoutData | undefined => {
  let firstname = '';
  let lastname = '';

  if (patient.firstname && patient.lastname) {
    firstname = patient.firstname;
    lastname = patient.lastname;
  } else {
    const payerFullName = event.payerName;

    if (payerFullName) {
      const [payerFirstName, ...payerSecondNames] = payerFullName.split(' ');

      firstname = payerFirstName.trim();
      lastname = payerSecondNames.join(' ').trim();
    }
  }

  if (firstname === '' || lastname === '') {
    return;
  }

  const {
    payerPhone: phone = '',
    shippingAddress: {
      addressLine,
      city,
      postalCode: zipcode,
      region: state,
    } = {},
    token,
  } = event;

  const method = 'apple-pay';
  const vendor = 'stripe';

  return {
    address1: addressLine?.[0],
    address2: addressLine?.[1],
    city,
    firstname,
    lastname,
    method,
    paymentNonce: token.id,
    ...(token.card
      ? {
          cardLastFour: token.card?.last4,
          cardFundingType: token.card?.funding,
        }
      : {}),
    phone,
    state,
    vendor,
    zipcode,
  };
};

const APPLE_PAY_ERROR_FALLBACK =
  'There was an error adding Apple Pay. Please try another payment method.';

const setPaymentRequestButtonHandlers = (
  flashMessages: FlashMessageProps[],
  patient: Patient,
  paymentRequest: stripe.paymentRequest.StripePaymentRequest,
  paymentRequestButton: stripe.elements.Element,
  selectedItemIds: number[],
  setFlashMessages: React.Dispatch<React.SetStateAction<FlashMessageProps[]>>,
  onSubmit: (args: {
    brand: Brand;
    data: ActivateExpressCheckoutData;
    treatmentCategory: TreatmentCategory;
  }) => Promise<void>,
  treatmentCategory: TreatmentCategory,
  brand: Brand,
  showFlashMessageOnCheckoutSuccess?: boolean,
  onSuccess?: () => void,
  onIneligible?: (ineligibleReason: IneligibleReasonType) => void,
) => {
  paymentRequestButton.on('click', async () => {
    if (selectedItemIds.length > 0) {
      trackEvent(AnalyticsEvent.APPLE_PAY_CLICKED);

      await ProductSelectionAndCheckoutService.updateSelectedItems(
        brand,
        selectedItemIds,
        treatmentCategory,
      );
    } else {
      setFlashMessages([
        ...flashMessages,
        {
          children: 'Please select at least one product to proceed.',
          icon: <ErrorIcon />,
          iconSize: 'sm',
          state: 'critical',
          title: 'Whoops!',
        },
      ]);
    }
  });

  paymentRequest.on('cancel', () => {
    trackEvent(AnalyticsEvent.APPLE_PAY_CANCELED);
  });

  // eslint-disable-next-line sonarjs/cognitive-complexity
  paymentRequest.on('token', async (event) => {
    if (event.shippingAddress) {
      try {
        const activateExpressCheckoutTrialData =
          getActivateExpressCheckoutTrialData(patient, event);

        if (activateExpressCheckoutTrialData) {
          const registrationEligibilityResponse =
            await updateRegistrationEligibility(brand, {
              state: activateExpressCheckoutTrialData.state,
              treatmentCategories: treatmentCategory,
            });

          if (registrationEligibilityResponse?.ineligibleReason) {
            if (onIneligible) {
              onIneligible(registrationEligibilityResponse.ineligibleReason);
            }

            event.complete('fail');

            return false;
          }

          await onSubmit({
            brand,
            data: activateExpressCheckoutTrialData,
            treatmentCategory,
          });

          event.complete('success');

          trackEvent(AnalyticsEvent.APPLE_PAY_SUCCEEDED);
          trackEvent(AnalyticsEvent.CARD_SAVED, {
            card_type: event.token.card?.funding || '',
          });

          if (onSuccess) {
            if (showFlashMessageOnCheckoutSuccess) {
              renderFlashMessageOnCheckoutSuccess(
                flashMessages,
                setFlashMessages,
              );
            }

            onSuccess();
          }
        } else {
          setFlashMessages([
            ...flashMessages,
            {
              children:
                'Please edit the name associated with Apple Pay so that there is both a first name and last name, and then try again. If you think this message is in error, please contact our Customer Success team at hello@curology.com.',
              icon: <ErrorIcon />,
              iconSize: 'sm',
              state: 'critical',
              title: 'Oops!',
            },
          ]);
        }
      } catch (exception) {
        event.complete('fail');

        trackEvent(AnalyticsEvent.APPLE_PAY_FAILED);

        reportUnhandledPaymentErrorToSentry(exception);
        const err = exception as HttpError<ValidationErrorResponse>;
        const message = getPaymentErrorMessage(
          err?.response?.data,
          APPLE_PAY_ERROR_FALLBACK,
        );

        setFlashMessages([
          ...flashMessages,
          {
            children: message,
            icon: <ErrorIcon />,
            iconSize: 'sm',
            state: 'critical',
            title: 'Whoops!',
          },
        ]);
      }
    }
  });
};

const createChildElementForApplePayButton = () => {
  if (!document.querySelector(`#${applePayButtonId}`)) {
    const applePayButtonElem = document.createElement('div');

    applePayButtonElem.setAttribute('id', applePayButtonId);

    document
      .querySelector(`#${applePayButtonContainerId}`)
      ?.appendChild(applePayButtonElem);
  }
};

export const renderApplePayButton = async (
  brand: Brand,
  stripeClient: stripe.Stripe,
  flags: Flags,
  flashMessages: FlashMessageProps[],
  futureShipmentTotal: number,
  handling: number,
  patient: Patient,
  selectedItemIds: number[],
  setCanUseApplePay: React.Dispatch<React.SetStateAction<boolean>>,
  setFlashMessages: React.Dispatch<React.SetStateAction<FlashMessageProps[]>>,
  shipping: number,
  tax: number,
  trialDiscountTotal: number,
  trialTotalWithoutDiscounts: number,
  trialTotalWithTax: number,
  onSubmit: (args: {
    brand: Brand;
    data: ActivateExpressCheckoutData;
    treatmentCategory: TreatmentCategory;
  }) => Promise<void>,
  treatmentCategory: TreatmentCategory,
  showFlashMessageOnCheckoutSuccess?: boolean,
  onSuccess?: () => void,
  onIneligible?: (ineligibleReason: IneligibleReasonType) => void,
) => {
  if (stripeClient) {
    const paymentRequestOptions = getPaymentRequestOptions(
      brand,
      flags,
      futureShipmentTotal,
      handling,
      shipping,
      tax,
      trialDiscountTotal,
      trialTotalWithoutDiscounts,
      trialTotalWithTax,
    );

    const paymentRequest = stripeClient.paymentRequest(paymentRequestOptions);
    const canMakePaymentResponse = await paymentRequest.canMakePayment();

    if (canMakePaymentResponse?.applePay) {
      trackEvent(AnalyticsEvent.APPLE_PAY_SHOWN);

      setCanUseApplePay(true);

      const paymentRequestButton = stripeClient
        .elements()
        .create('paymentRequestButton', {
          paymentRequest,
          style: {
            paymentRequestButton: applePayButtonStyleConfig,
          },
        });

      if (paymentRequestButton) {
        setPaymentRequestButtonHandlers(
          flashMessages,
          patient,
          paymentRequest,
          paymentRequestButton,
          selectedItemIds,
          setFlashMessages,
          onSubmit,
          treatmentCategory,
          brand,
          showFlashMessageOnCheckoutSuccess,
          onSuccess,
          onIneligible,
        );

        createChildElementForApplePayButton();

        paymentRequestButton.mount(`#${applePayButtonId}`);
      }
    }
  }
};
